/**
 * Composite grading / validation for multiple FITB exercises per content item.
 */

import { fitbGrader, type FITBAnswer } from '@ai-content-hub/content-elements';
import type { GradingResult, ValidationResult } from '@ai-content-hub/content-kit';

import type { FITBContentData } from './schema';
import { normalizeFITBContentData } from './schema';

export type FITBContentCompositeAnswer = Record<string, FITBAnswer>;

export function normalizeFitbContentAnswer(
    answer: unknown,
    exerciseIds: string[],
): FITBContentCompositeAnswer {
    if (!answer || typeof answer !== 'object' || exerciseIds.length === 0) {
        return {};
    }
    if (Array.isArray(answer)) {
        return {};
    }
    const o = answer as Record<string, unknown>;
    const keys = Object.keys(o);
    if (exerciseIds.length === 1 && keys.some((k) => k.startsWith('blank-'))) {
        return { [exerciseIds[0]]: o as FITBAnswer };
    }
    const out: FITBContentCompositeAnswer = {};
    for (const id of exerciseIds) {
        const v = o[id];
        if (v && typeof v === 'object' && !Array.isArray(v)) {
            out[id] = v as FITBAnswer;
        }
    }
    return out;
}

export function gradeFITBContentComposite(
    data: FITBContentData,
    answer: unknown,
    config?: Record<string, unknown>,
): GradingResult {
    const normalized = normalizeFITBContentData(data);
    const ids = normalized.exercises.map((e) => e.id);
    const byEx = normalizeFitbContentAnswer(answer, ids);
    let score = 0;
    let max = 0;
    let allCorrect = true;
    const perExercise: GradingResult[] = [];
    for (const ex of normalized.exercises) {
        const r = fitbGrader.grade(ex.data, byEx[ex.id] ?? {}, config);
        perExercise.push(r);
        score += r.score;
        max += r.max;
        if (!r.isCorrect) {
            allCorrect = false;
        }
    }
    return {
        isCorrect: allCorrect && normalized.exercises.length > 0,
        score,
        max,
        // EAA-2067: no hard-coded English feedback ("Correct." / "Some answers need review.")
        // — ContentPlayer falls back to its localized t('correct') / t('incorrect').
        feedback: undefined,
        details: { perExercise },
    };
}

export function validateFITBContentComposite(data: FITBContentData): ValidationResult {
    const normalized = normalizeFITBContentData(data);
    const errors: ValidationResult['errors'] = [];
    normalized.exercises.forEach((ex, i) => {
        const v = fitbGrader.validate?.(ex.data);
        if (v && !v.isValid) {
            for (const e of v.errors) {
                errors.push({
                    field: `exercises.${i}.data.${e.field}`,
                    message: e.message,
                    code: e.code,
                });
            }
        }
    });
    return { isValid: errors.length === 0, errors };
}
